import { useEffect, useRef, useState } from "react";

// When a field's validation warning is allowed to show. Shared by the in-line merge review
// (useImportFieldWarning / useDateFieldWarning, reveal from the merge store) and by the profile
// editor (useProfileFieldWarning, reveal from ProfileRevealContext) so both surfaces have identical
// timing: the warning appears once the user LEAVES the field, or when a blocked save reveals every
// error at once — never mid-keystroke, which would nag at the first typed character.
//
// `error` is whatever the caller computed; passing null keeps the field quiet (used for values that
// would not be committed anyway, e.g. the discarded side of a merge pair).
export function useFieldWarning<E>(
  error: E | null,
  reveal: boolean,
): { error: E | null; onBlur: () => void; onEdit: () => void } {
  const [blurred, setBlurred] = useState(false);
  const [editing, setEditing] = useState(false);
  // A save is blocked at the moment the user asks to save, which is precisely when they are NOT
  // typing — so the reveal has to defeat the mid-edit suppression, or the one field they typed into
  // last (never blurred, so `editing` is still set) would be the one field that stays silent. Only
  // the transition matters: once revealed, resuming typing suppresses this field's warning again.
  const wasRevealed = useRef(reveal);
  useEffect(() => {
    if (reveal && !wasRevealed.current) setEditing(false);
    wasRevealed.current = reveal;
  }, [reveal]);
  return {
    error: error && !editing && (blurred || reveal) ? error : null,
    onBlur: () => {
      setEditing(false);
      setBlurred(true);
    },
    onEdit: () => setEditing(true),
  };
}
